'use client';

import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowUpRight, Gift, Loader2, Wallet } from 'lucide-react';
import { format, isSameDay, isValid } from 'date-fns';

import { Sheet, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuthStore, usePaymentStore, useShoppingWalletStore } from '@/presentation/hooks/useStores';
import { safeRedirectPath, storePostAuthRedirect } from '@/shared/utils/postAuthRedirect';
import { openWhatsApp, SUPPORT_CONTACTS } from '@/shared/utils/externalLinks';
import type { ShoppingWalletActivityItem, ShoppingWalletActivityStatus } from '@/presentation/stores/ShoppingWalletStore';
import { getNextWalletExpiryRefreshDelay, getWalletExpiryPresentation } from '@/shared/utils/walletExpiry';
import { getWalletCreditTerms, WalletCreditTerms } from './WalletCreditTerms';
import { WalletExpiryLabel } from './WalletExpiryLabel';
import { WalletSheetLayout } from './WalletSheetLayout';

interface ShoppingWalletDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatMoney(currency: string, amount: string | number): string {
  const numeric = typeof amount === 'number' ? amount : Number(amount || 0);
  return `${currency} ${numeric.toFixed(2)}`;
}

function WalletTimestamp({ value }: { value: string }) {
  const date = new Date(value);
  if (!value || !isValid(date)) return <span>{value}</span>;
  const fullTimestamp = format(date, 'd MMM yyyy, h:mma').replace(/AM|PM/g, (match) => match.toLowerCase());
  const label = isSameDay(date, new Date())
    ? format(date, 'h:mma').toLowerCase()
    : format(date, 'd MMM yyyy');
  return <time dateTime={date.toISOString()} aria-label={fullTimestamp}>{label}</time>;
}

type DisplayStatus = ShoppingWalletActivityStatus | 'unavailable';

function badgeClasses(status: DisplayStatus): string {
  if (status === 'active') return 'bg-[#F0FDF4] text-[#16A34A]';
  if (status === 'used') return 'bg-[#F3F4F6] text-[#4B5563]';
  if (status === 'expired') return 'bg-[#FEF2F2] text-[#B91C1C]';
  return 'bg-[#F3F4F6] text-[#6B7280]';
}

function badgeLabel(status: DisplayStatus): string {
  if (status === 'active') return 'Active';
  if (status === 'used') return 'Used';
  if (status === 'expired') return 'Expired';
  if (status === 'unavailable') return 'Unavailable';
  return 'Cancelled';
}

function ActivityIcon({ item }: { item: ShoppingWalletActivityItem }) {
  if (item.kind === 'usage') {
    return (
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6]">
        <ArrowUpRight className="size-5 text-[#4B5563]" />
      </div>
    );
  }

  const normalizedTitle = item.title.toLowerCase();
  const isGift = normalizedTitle.includes('gift') || normalizedTitle.includes('welcome');
  const Icon = isGift ? Gift : Wallet;
  const iconTone = isGift ? 'text-[#D97706]' : 'text-[#16A34A]';
  const background = isGift ? 'bg-[#FEF3C7]' : 'bg-[#F0FDF4]';

  return (
    <div className={`flex size-10 shrink-0 items-center justify-center rounded-full ${background}`}>
      <Icon className={`size-5 ${iconTone}`} />
    </div>
  );
}

function ActivityRow({ item, nowMs }: { item: ShoppingWalletActivityItem; nowMs: number }) {
  const amountTone = item.direction === 'in' ? 'text-[#16A34A]' : 'text-[#141414]';
  const amountPrefix = item.direction === 'in' ? '+' : '-';
  const expiry = getWalletExpiryPresentation(item.expiresAt, nowMs);
  const effectiveStatus: DisplayStatus = item.status === 'active' && expiry.state === 'expired'
    ? 'expired'
    : item.status === 'active' && expiry.state === 'invalid' ? 'unavailable' : item.status;
  const showActiveDetails = item.kind === 'credit' && item.status === 'active';
  const showTerms = showActiveDetails && expiry.permitsActiveTerms;
  const displayedTerms = showTerms ? getWalletCreditTerms(item) : null;
  const hasDisplayedTerms = Boolean(displayedTerms && (displayedTerms.conditions || displayedTerms.minimum !== null));

  return (
    <div data-testid="wallet-activity-row" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <ActivityIcon item={item} />
          <div className="min-w-0">
            <p className="truncate text-[15px] font-medium leading-tight text-[#141414]">{item.title}</p>
            <p className="text-[13px] text-[#777777]"><WalletTimestamp value={item.subtitle || item.timestamp} /></p>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p className={`text-[15px] font-medium ${amountTone}`}>
            {amountPrefix}
            {formatMoney(item.currency, item.amount)}
          </p>
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${badgeClasses(effectiveStatus)}`}>
            {badgeLabel(effectiveStatus)}
          </span>
        </div>
      </div>

      {showTerms ? <WalletCreditTerms item={item} /> : null}

      {showActiveDetails ? (
        <div className={hasDisplayedTerms ? '-mt-2' : undefined}>
          <WalletExpiryLabel expiry={expiry} />
        </div>
      ) : null}
    </div>
  );
}

const ShoppingWalletDrawer = observer(function ShoppingWalletDrawer({
  open,
  onOpenChange,
}: ShoppingWalletDrawerProps) {
  const authStore = useAuthStore();
  const paymentStore = usePaymentStore();
  const router = useRouter();
  const shoppingWalletStore = useShoppingWalletStore();
  const signingIn = useRef(false);
  const [signInState, setSignInState] = useState<'idle' | 'clearing' | 'failed' | 'support'>('idle');
  const signInAgain = async () => {
    if (signingIn.current || authStore.isSigningOut || !authStore.isHydrated || authStore.isLoading || paymentStore.isInitializingPayment) return;
    if (paymentStore.hasUnsavedCheckoutObservation()) {
      setSignInState('support');
      return;
    }
    const path = safeRedirectPath(`${window.location.pathname}${window.location.search}${window.location.hash}`) ?? '/';
    storePostAuthRedirect(path);
    signingIn.current = true;
    setSignInState('clearing');
    try {
      await authStore.signOut({ preserveCheckoutObservation: true });
      if (authStore.isAuthenticated || authStore.currentUser) {
        setSignInState('failed');
        return;
      }
      onOpenChange(false);
      router.replace(`/sign-in?redirect=${encodeURIComponent(path)}`);
    } catch {
      setSignInState('failed');
    } finally {
      signingIn.current = false;
    }
  };
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [retrying, setRetrying] = useState(false);
  const [retryingFullFailure, setRetryingFullFailure] = useState(false);
  const retryWallet = async () => {
    setRetryingFullFailure(Boolean(shoppingWalletStore.balanceError && shoppingWalletStore.historyError));
    setRetrying(true);
    try {
      await shoppingWalletStore.loadWallet();
    } finally {
      setRetrying(false);
      setRetryingFullFailure(false);
    }
  };
  const fullFailure = !shoppingWalletStore.hasLoaded && !shoppingWalletStore.hasLoadedHistory
    && Boolean((shoppingWalletStore.balanceError && shoppingWalletStore.historyError) || retryingFullFailure);
  const showRetryProgress = retrying && !shoppingWalletStore.canRetry;
  const needsSignIn = shoppingWalletStore.balanceFailure?.kind === 'authentication_required'
    || shoppingWalletStore.historyFailure?.kind === 'authentication_required';
  const retryButton = (
    <>
    {needsSignIn || signInState !== 'idle' ? (
      <button type="button" data-testid="shopping-wallet-sign-in"
        disabled={signInState === 'clearing' || authStore.isSigningOut || !authStore.isHydrated || authStore.isLoading || paymentStore.isInitializingPayment}
        onClick={() => { void signInAgain(); }}
        className="min-h-11 rounded-md px-3 py-2 text-sm font-medium text-[#0046C8] underline underline-offset-4 disabled:opacity-60">
        {signInState === 'clearing' ? 'Preparing sign-in…' : 'Sign in again'}
      </button>
    ) : null}
    {needsSignIn && paymentStore.isInitializingPayment ? <p role="status">Wait for your payment request to finish before signing in again.</p> : null}
    {signInState === 'failed' ? <p role="alert">We couldn’t prepare sign-in. Please try again.</p> : null}
    {signInState === 'support' ? <div>
      <p role="alert">Your payment progress could not be saved. Keep this page open and contact support before signing in.</p>
      <button type="button" data-testid="shopping-wallet-sign-in-support" className="min-h-11 px-3 underline"
        onClick={() => openWhatsApp(SUPPORT_CONTACTS.BETTY_WHATSAPP, SUPPORT_CONTACTS.BETTY_DEFAULT_MESSAGE,
          { target: '_blank', windowFeatures: 'noopener,noreferrer' })}>Contact support</button>
    </div> : null}
    <button
      type="button"
      data-testid="shopping-wallet-retry"
      disabled={!shoppingWalletStore.canRetry || signInState === 'clearing'}
      onClick={() => { void retryWallet(); }}
      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[#0046C8] underline underline-offset-4 hover:text-[#003594] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-motitoBlue focus-visible:ring-offset-2 disabled:opacity-60"
    >
      {showRetryProgress ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
      <span role="status">{showRetryProgress ? 'Trying again…' : 'Try again'}</span>
    </button>
    </>
  );

  useEffect(() => {
    if (open && authStore.isAuthenticated) {
      // Gifts and usage can change while this drawer is closed, even if its
      // history was already loaded by the header or a previous visit.
      void shoppingWalletStore.loadWallet(true);
    }
  }, [open, authStore.isAuthenticated, shoppingWalletStore]);

  const activityItems = shoppingWalletStore.activityItems;
  const displayNowMs = open ? Math.max(nowMs, Date.now()) : nowMs;
  const expiryClockKey = JSON.stringify(activityItems
    .filter((item) => item.kind === 'credit' && item.status === 'active')
    .map((item) => item.expiresAt));

  useEffect(() => {
    if (!open) return undefined;
    const activeExpiryValues = JSON.parse(expiryClockKey) as Array<string | null | undefined>;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const scheduleRefresh = () => {
      const current = Date.now();
      const delay = getNextWalletExpiryRefreshDelay(activeExpiryValues, current);
      if (delay !== null) timeout = setTimeout(() => {
        const refreshedAt = Date.now();
        setNowMs(refreshedAt);
        if (activeExpiryValues.some((value) => {
          const expiryMs = value ? new Date(value).getTime() : NaN;
          return expiryMs > current && expiryMs <= refreshedAt;
        })) {
          void shoppingWalletStore.loadWallet(true);
        }
        scheduleRefresh();
      }, delay);
    };
    scheduleRefresh();
    return () => { if (timeout) clearTimeout(timeout); };
  }, [open, expiryClockKey, shoppingWalletStore]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <WalletSheetLayout open={open} header={
            <SheetHeader className="gap-1 px-5 pb-4 pt-0 text-left sm:pt-5">
              <SheetTitle className="pr-12 text-left text-[28px] font-semibold tracking-tight text-[#141414]">
                Shopping Wallet
              </SheetTitle>
              <p className="text-sm text-[#777777]">Your Motito balance for purchases</p>
            </SheetHeader>
      }>
              {fullFailure ? (
                <section aria-label="Wallet unavailable" className="space-y-2 px-2 pb-10 pt-11 text-center">
                  <div role="alert">
                    <h2 className="text-base font-medium text-[#333333]">We couldn’t load your wallet</h2>
                    <p className="mt-1 text-sm leading-5 text-[#666666]">
                      {needsSignIn ? 'Sign in again to view your Wallet.' : 'Please try again in a moment.'}
                    </p>
                  </div>
                  {retryButton}
                </section>
              ) : <>
              <section aria-label="Wallet balance" className="rounded-xl bg-[#141414] px-5 py-6 text-white shadow-sm">
                {shoppingWalletStore.hasLoaded ? (
                  <p className="text-[40px] font-semibold leading-none">
                    {formatMoney(shoppingWalletStore.currency, shoppingWalletStore.balance)}
                  </p>
                ) : (
                  <p role="status" className="text-lg font-medium">
                    {shoppingWalletStore.balanceError ? 'Balance unavailable' : 'Loading balance…'}
                  </p>
                )}
                {shoppingWalletStore.hasLoaded && shoppingWalletStore.isLoading ? (
                  <p role="status" className="mt-3 text-xs text-white/60">Refreshing balance…</p>
                ) : null}
                {shoppingWalletStore.balanceError && shoppingWalletStore.hasLoaded ? (
                  <p role="alert" className="mt-3 text-sm text-white/70">
                    Couldn’t refresh. Showing your last loaded balance.
                  </p>
                ) : null}
                {shoppingWalletStore.hasLoaded ? (
                  <p className="mt-3 text-xs text-white/60">
                    Terms and conditions apply.
                  </p>
                ) : null}
              </section>

              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-[#141414]">Activity</h2>
                  {shoppingWalletStore.isHistoryLoading ? (
                    <span role="status" className="text-xs text-[#777777]">
                      {shoppingWalletStore.hasLoadedHistory ? 'Refreshing activity…' : 'Loading activity…'}
                    </span>
                  ) : null}
                </div>

                {activityItems.length > 0 ? (
                  <div className="space-y-4">
                    {activityItems.map((item) => (
                      <div
                        key={item.id}
                        className={`rounded-xl border border-[#E8E8E8] bg-white px-4 pt-3 shadow-sm ${item.kind === 'credit' && item.status === 'active' ? 'pb-2' : 'pb-3'}`}
                      >
                        <ActivityRow item={item} nowMs={displayNowMs} />
                      </div>
                    ))}
                  </div>
                ) : shoppingWalletStore.isHistoryLoading ? (
                  <div aria-hidden="true" className="space-y-3">
                    <div className="h-24 animate-pulse rounded-xl bg-white/70" />
                    <div className="h-24 animate-pulse rounded-xl bg-white/70" />
                  </div>
                ) : !shoppingWalletStore.historyError ? (
                  <div className="rounded-xl border border-dashed border-[#D9D9D9] bg-white px-4 py-5 text-sm text-[#777777]">
                    {shoppingWalletStore.hasLoadedHistory && !shoppingWalletStore.historyError
                      ? 'No wallet activity yet.'
                      : 'Activity unavailable.'}
                  </div>
                ) : null}
                {shoppingWalletStore.historyError ? (
                  <p role="alert" className="text-sm text-[#666666]">
                    {shoppingWalletStore.hasLoadedHistory
                      ? 'Couldn’t refresh. Showing your last loaded activity.'
                      : 'We couldn’t load your activity.'}
                  </p>
                ) : null}
              </section>

              {needsSignIn ? <p role="alert" className="text-sm text-[#666666]">Sign in again to refresh your Wallet.</p> : null}
              {shoppingWalletStore.error || retrying ? retryButton : null}
              </>}

              <p className="text-center text-xs leading-5 text-[#666666]">
                Credits can only be used for purchases on Motito.
              </p>
      </WalletSheetLayout>
    </Sheet>
  );
});

ShoppingWalletDrawer.displayName = 'ShoppingWalletDrawer';

export default ShoppingWalletDrawer;
